// The Shop tab (005 FR-020): the categories as Live Colour Tiles leading to each orderable category;
// a category the server marks not orderable (Uniforms) stays "Coming soon" and isn't pressable.
import { Link } from "expo-router";
import { ScrollView, StyleSheet, View } from "react-native";

import { useChromeKind } from "@/shell/chrome/useChromeKind";
import { SHOP_COPY as COPY } from "@/shop/copy";
import { isOrderable, useCategories } from "@/shop/useCategories";
import { makeStyles } from "@/theme/makeStyles";
import { Button } from "@/ui/Button";
import { Text } from "@/ui/Text";

import { Tile } from "./HomeScreen";
import { BookStackArt, PencilArt, ShirtArt } from "./TileArt";

export const SHOP_COPY = {
  title: COPY.categoriesTitle,
  lead: COPY.categoriesLead,
  soon: COPY.comingSoon,
  home: "Go to Home",
} as const;

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    scroll: { flex: 1, backgroundColor: t.color.background },
    page: {
      width: "100%",
      maxWidth: 1080,
      alignSelf: "center",
      paddingHorizontal: t.space.lg,
      paddingTop: t.space.lg,
      paddingBottom: t.space.x3l,
      gap: t.space.sm,
    },
    intro: { gap: t.space.xs, marginBottom: t.space.md },
    row: { flexDirection: "row", gap: t.space.sm },
    action: { marginTop: t.space.lg, alignSelf: "flex-start", minWidth: 200 },
  }),
);

export function ShopScreen() {
  const styles = useStyles();
  const wide = useChromeKind() === "desktop";
  const categories = useCategories();
  const booksOn = isOrderable(categories, "SCHOOL_BOOKS");
  const stationeryOn = isOrderable(categories, "STATIONERY");
  const books = (
    <Tile
      testID="shop-books"
      index={0}
      label="School books"
      caption={booksOn ? "By class and subject" : SHOP_COPY.soon}
      fill="tileAmber"
      ink="onTileAmber"
      Art={BookStackArt}
      href={booksOn ? "/shop/SCHOOL_BOOKS" : undefined}
    />
  );
  const stationery = (
    <Tile
      testID="shop-stationery"
      index={1}
      label="Stationery"
      caption={stationeryOn ? "Notebooks, pens & more" : SHOP_COPY.soon}
      fill="tileGreen"
      ink="onTile"
      Art={PencilArt}
      href={stationeryOn ? "/shop/STATIONERY" : undefined}
    />
  );
  const uniforms = (
    <Tile
      testID="shop-uniforms"
      index={2}
      label="Uniforms"
      caption={SHOP_COPY.soon}
      fill="tileMuted"
      ink="onTileMuted"
      Art={ShirtArt}
    />
  );
  return (
    <ScrollView style={styles.scroll} testID="shop-screen">
      <View style={styles.page} role="main">
        <View style={styles.intro}>
          <Text variant="display" accessibilityRole="header" aria-level={1}>
            {SHOP_COPY.title}
          </Text>
          <Text tone="muted">{SHOP_COPY.lead}</Text>
        </View>
        {wide ? (
          <View style={styles.row}>
            {books}
            {stationery}
            {uniforms}
          </View>
        ) : (
          <>
            <View style={styles.row}>
              {books}
              {stationery}
            </View>
            <View style={styles.row}>{uniforms}</View>
          </>
        )}
        {wide ? (
          <View style={styles.action}>
            <Link href="/" asChild>
              <Button variant="outline" accessibilityRole="link">
                {SHOP_COPY.home}
              </Button>
            </Link>
          </View>
        ) : null}
      </View>
    </ScrollView>
  );
}
